import { apiFetch } from "./client";

/**
 * D-043 S2 — notification feed API.
 *
 * List/mark-read go through /Custom/frontend/* (own-user read-model WITHOUT
 * the stock auto-mark-read side effect — stock GET /Notification marks every
 * fetched row as read, breaking explicit read-state). Count + mark-all stay
 * on the stock endpoints.
 */

export type NotificationSeverity = "critical" | "warning" | "info" | "reminder";

export interface NotificationRow {
  id: string;
  read: boolean;
  createdAt: string;
  kind: "engine" | "legacy";
  redacted: boolean;
  eventType: string | null;
  severity: NotificationSeverity | null;
  severityRank: number;
  workSurface: string | null;
  sourceModule: string | null;
  sourceEntityType: string | null;
  sourceEntityId: string | null;
  deepLink: string | null;
  title: string;
  subtitle: string | null;
}

export interface NotificationFeed {
  list: NotificationRow[];
  total: number;
  unreadTotal: number;
  offset: number;
  maxSize: number;
}

export interface NotificationFeedFilters {
  view?: "all" | "unread";
  severity?: NotificationSeverity[];
  source?: string[];
  offset?: number;
  limit?: number;
}

export function fetchNotificationFeed(
  filters: NotificationFeedFilters = {},
  signal?: AbortSignal,
): Promise<NotificationFeed> {
  return apiFetch<NotificationFeed>("/Custom/frontend/notifications", {
    query: {
      view: filters.view,
      severity: filters.severity?.length ? filters.severity.join(",") : undefined,
      source: filters.source?.length ? filters.source.join(",") : undefined,
      offset: filters.offset,
      limit: filters.limit,
    },
    signal,
  });
}

/** Stock endpoint — unread badge count (NOT the Home/F6 action count). */
export function fetchNotReadCount(signal?: AbortSignal): Promise<number> {
  // RPC-style controller action (Espo 9.3.6: getActionNotReadCount) —
  // /Notification/notReadCount БЕЗ /action/ матчиться як record-read і дає 404.
  return apiFetch<number>("/Notification/action/notReadCount", { signal });
}

export function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/Custom/frontend/notifications/${encodeURIComponent(id)}/mark-read`,
    { method: "POST" },
  );
}

/**
 * Stock endpoint — personal recipient read-state only; does NOT close
 * tasks/requests/approvals (Notification ≠ Task).
 */
export function markAllNotificationsRead(): Promise<boolean> {
  return apiFetch<boolean>("/Notification/action/markAllRead", { method: "POST", body: {} });
}

/* ---------------------------------------------------------------- prefs */

export interface NotificationPreferenceGroup {
  groupKey: string;
  eventTypes: string[];
  inApp: boolean;
  email: boolean;
}

export interface NotificationPreferences {
  configurable: NotificationPreferenceGroup[];
  mandatory: {
    eventTypes: string[];
    emailEventTypes: string[];
  };
}

export function fetchNotificationPreferences(
  signal?: AbortSignal,
): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>("/Custom/frontend/notification-preferences", {
    signal,
  });
}

export function putNotificationPreferenceGroup(input: {
  groupKey: string;
  channel: "in_app" | "email";
  value: boolean;
}): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>("/Custom/frontend/notification-preferences", {
    method: "PUT",
    body: input,
  });
}
